import { type ReactNode } from 'react'
import { TableHost } from '@/components/Table'
import { TableScroller } from '@/components/Table/TableScroller'
import {
  ClickableImageCell,
  ClickableTable,
  ClickableTableBody,
  ClickableTableCell,
  ClickableTableHead,
  ClickableTableHeader,
  ClickableTableRow,
  ClickableTextCell,
} from '@/components/Table/ClickableTable'
import { TableSearchbar } from '@/components/TableSearchbar'
import { match } from '@/utils/pattern-match-util'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { PillCell } from '@/components/Table/PillCell'
import { formatDate } from 'date-fns'
import { type Id } from '@/infra/id'
import { type StatusOrActions } from '@/features/admin/account-linking/AccountMergeRequestItemViewModel'

export interface MergeRequestsTableProps {
  searchText: string
  onSearchTextChange: (text: string) => void
  statusOrActions: StatusOrActions
  /**
   * The infinite scroll loader, rendered below the table while more requests can be fetched.
   */
  loader?: ReactNode
  children?: ReactNode
}

export function MergeRequestsTable({
  searchText,
  onSearchTextChange,
  statusOrActions,
  loader,
  children,
}: MergeRequestsTableProps) {
  return (
    <TableHost>
      <TableSearchbar
        searchText={searchText}
        onSearchTextChange={onSearchTextChange}
      />
      <TableScroller>
        <ClickableTable striped>
          <ClickableTableHead>
            <ClickableTableHeader alignment="left">Name</ClickableTableHeader>
            <ClickableTableHeader alignment="left">Email</ClickableTableHeader>
            <ClickableTableHeader alignment="left">
              Primary ID
            </ClickableTableHeader>
            <ClickableTableHeader alignment="left">
              Duplicate ID
            </ClickableTableHeader>
            <ClickableTableHeader alignment="left">
              Date requested
            </ClickableTableHeader>
            <StatusOrActionsHeader statusOrActions={statusOrActions} />
          </ClickableTableHead>
          <ClickableTableBody>{children}</ClickableTableBody>
        </ClickableTable>
        {loader}
      </TableScroller>
    </TableHost>
  )
}

/**
 * Props for the table's status or actions menu column.
 */
interface StatusOrActionsHeaderProps {
  statusOrActions: StatusOrActions
}

/**
 * The table's status or (invisible) actions menu column.
 */
function StatusOrActionsHeader({
  statusOrActions,
}: StatusOrActionsHeaderProps) {
  return match(statusOrActions, {
    Status: () => (
      <ClickableTableHeader alignment="left">Status</ClickableTableHeader>
    ),
    Actions: () => <ClickableTableHeader alignment="right" />,
  })
}

/**
 * Props for the Merge Request Row.
 */
export interface MergeRequestRowProps {
  hubspotLink: string
  displayName: string
  avatar?: string
  avatarFallback: string | null
  email: string
  primaryAccountId: Id
  duplicateAccountId: Id
  dateCreated: Date
  statusOrActions: StatusOrActions
  displayStatus: string
  onMerge: () => unknown
  onReject: () => unknown
  onIgnore: () => unknown
}

/**
 * An individual merge request row/record.
 */
export function MergeRequestRow({
  hubspotLink,
  displayName,
  avatar,
  avatarFallback,
  email,
  primaryAccountId,
  duplicateAccountId,
  dateCreated,
  statusOrActions,
  displayStatus,
  onMerge,
  onReject,
  onIgnore,
}: MergeRequestRowProps) {
  return (
    <ClickableTableRow href={hubspotLink} target="_blank" alwaysShowPointer>
      <ClickableImageCell
        title={displayName}
        image={avatar}
        imageFallback={avatarFallback}
      />
      <ClickableTableCell>{email}</ClickableTableCell>
      <ClickableTableCell>{primaryAccountId}</ClickableTableCell>
      <ClickableTableCell>{duplicateAccountId}</ClickableTableCell>
      <ClickableTableCell>{formatDate(dateCreated, 'PP')}</ClickableTableCell>
      <StatusOrActionsCell
        statusOrActions={statusOrActions}
        displayStatus={displayStatus}
        onMerge={onMerge}
        onReject={onReject}
        onIgnore={onIgnore}
      />
    </ClickableTableRow>
  )
}

/**
 * Props for the Status Or Actions Cell.
 */
interface StatusOrActionsProps {
  statusOrActions: StatusOrActions
  displayStatus: string
  onMerge: () => unknown
  onReject: () => unknown
  onIgnore: () => unknown
}

/**
 * A cell containing either the merge request's status or its actions menu.
 */
function StatusOrActionsCell({
  statusOrActions,
  displayStatus,
  onMerge,
  onReject,
  onIgnore,
}: StatusOrActionsProps) {
  return match(statusOrActions, {
    Status: () => <PillCell>{displayStatus}</PillCell>,
    Actions: () => (
      <ClickableTextCell alignment="right">
        <MergeRequestActions
          onMerge={onMerge}
          onReject={onReject}
          onIgnore={onIgnore}
        />
      </ClickableTextCell>
    ),
  })
}

/**
 * Props for a merge request's actions.
 */
interface MergeRequestActionsProps {
  onMerge: () => unknown
  onReject: () => unknown
  onIgnore: () => unknown
}

/**
 * A merge request's actions menu.
 */
function MergeRequestActions({
  onMerge,
  onReject,
  onIgnore,
}: MergeRequestActionsProps) {
  return (
    <Dropdown
      alignContent="right"
      autoDismiss
      position="bottom-end"
      button={<Icon name="more" />}
    >
      <DropdownTitle>Actions</DropdownTitle>
      <DropdownItem onPress={onMerge}>Merge accounts</DropdownItem>
      <DropdownItem onPress={onReject}>Reject merge request</DropdownItem>
      <DropdownItem onPress={onIgnore}>Ignore merge request</DropdownItem>
    </Dropdown>
  )
}
